import * as Sentry from '@sentry/react'

import {
  Component,
  FastenerData,
  Item,
  MountingCalcResult,
  ProductCode,
  QuantityPerProduct,
  RoofTypeName,
} from './types'

import { cloneDeep, isEqual } from 'lodash'
import type { ModuleLayout } from 'types/global'

const KEY_TOKEN = 'token'

export const getRoofName = (url): RoofTypeName | null => {
  if (url) {
    let roofId = Number(url.split('roof_types/')[1].split('/')[0])
    return window.AccountHelper.getRoofTypeById(roofId)?.name
  } else {
    return null
  }
}

// Mounting system implementations provide MountingItems at various points in their calculations
export function createMountingItem<T extends Item>(
  precursor: T,
  { top = 0, left = 0, blockIndex = undefined as number | undefined } = {}
): T {
  return {
    ...precursor,
    components: precursor.components.map((component) => ({
      ...component,
      left: component.left + left, // precursor comes with relative positions for each component
      top: component.top + top, // so we move everything to its absolute position on the roof
      blockIndex,
    })),
  }
}

export function addItems<T extends MountingCalcResult>(result: T, ...setsOfnNewItems: Item[][]): T {
  return {
    ...result,
    items: [...result.items, ...setsOfnNewItems.flat()],
  }
}

export function getMatchingComponents(result: MountingCalcResult, expectedProperties: Partial<Component>) {
  return result.items
    .flatMap((item) => item.components)
    .filter((component) =>
      Object.keys(expectedProperties).every((propName) => component[propName] === expectedProperties[propName])
    )
}

export function saveFastenerData(system, fastener: FastenerData, roofIndex: number): void {
  var fastenerData: any = {
    fixing_length_screwed_through_timber: fastener.length - 25, // The screw first goes through about 5mm of roof hook, then about 20mm of batten or wooden spacer
    fixing_diameter: fastener.diameter,
  }
  if (fastener.qty) fastenerData.fixing_qty = fastener.qty
  saveDataToRoofFacet(window.projectForm, roofIndex, fastenerData)
}

// TODO: Custom data does not differentiate between systems, so data is constantly getting overwritten
// This should be refactored so mounting data is saved per system per roof facet
export function saveDataToRoofFacet(form, roofIndex: number, roofFacetData: Record<string, any>): void {
  const originalCustomData = cloneDeep(form.getState().values?.custom_data || {})
  const updatedCustomData = cloneDeep(originalCustomData)

  if (!updatedCustomData._mcs) updatedCustomData._mcs = {}
  if (!updatedCustomData._mcs.roof_facets) updatedCustomData._mcs.roof_facets = []
  if (!updatedCustomData._mcs.roof_facets[roofIndex]) {
    updatedCustomData._mcs.roof_facets[roofIndex] = {}
  }

  Object.assign(updatedCustomData._mcs.roof_facets[roofIndex], roofFacetData)

  const fieldsAreDirty = !isEqual(originalCustomData, updatedCustomData)
  if (fieldsAreDirty) {
    form.mutators.updateField('custom_data', updatedCustomData)
    if (form.mutators.markFieldAsDirty) {
      form.mutators.markFieldAsDirty('custom_data')
    }
  }
}

function getArchivedComponentCodes(): Set<string> {
  const allCodes = new Set(window.AccountHelper.getComponentOtherSpecsAvailable(true).map((c) => c.code))
  const activeCodes = new Set(window.AccountHelper.getComponentOtherSpecsAvailable().map((c) => c.code))
  const archived = new Set<string>()
  allCodes.forEach((code) => {
    if (!activeCodes.has(code)) archived.add(code)
  })
  return archived
}

export function addItemsToSystem(system, manufacturer: string, result: MountingCalcResult): void {
  var componentsWithQty = {}
  result.items.forEach((item) => {
    if (!item.name) return
    if (componentsWithQty[item.name]) {
      componentsWithQty[item.name].qty += 1
    } else {
      componentsWithQty[item.name] = { qty: 1 }
    }
  })

  // Filter out archived components before activation check or adding to system
  const archivedCodes = getArchivedComponentCodes()
  const archivedCodesInBom = Object.keys(componentsWithQty).filter((code) => archivedCodes.has(code))

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    'MOUNTING_ARCHIVED_COMPONENTS',
    system.uuid,
    'mounting-integration'
  )

  if (archivedCodesInBom.length > 0) {
    archivedCodesInBom.forEach((code) => delete componentsWithQty[code])

    showArchivedComponentsWarning(system.uuid, archivedCodesInBom)

    Sentry.captureMessage('Mounting automation skipped archived components', {
      tags: { component: 'mounting', systemId: system.uuid },
      extra: {
        archivedComponentCodes: archivedCodesInBom.join(', '),
        archivedComponentCount: archivedCodesInBom.length,
        manufacturer,
      },
      level: 'warning',
    })
  }

  if (Object.keys(componentsWithQty).length === 0) return

  var otherComponentActivationCodes = window.AccountHelper.getComponentOtherSpecsAvailable(true).map((c) => c.code)

  var otherComponentCodesToActivate = Object.keys(componentsWithQty).filter((componentCode) => {
    return !otherComponentActivationCodes.includes(componentCode)
  })

  function addComponentsToProject() {
    var otherComponentIdByCode = {}
    var otherComponentShowCustomer = {}
    window.AccountHelper.getComponentOtherSpecsAvailable().forEach((otherComponentType) => {
      otherComponentShowCustomer[otherComponentType.code] = otherComponentType.show_customer
      otherComponentIdByCode[otherComponentType.code] = otherComponentType.id
    })

    const getComponentActivationId = (code) =>
      otherComponentIdByCode.hasOwnProperty(code) ? otherComponentIdByCode[code] : null

    const authoredBy = `racking:${system.mounting}`

    Object.keys(componentsWithQty).forEach((componentCode) => {
      var other = new window.OsOther({
        code: componentCode,
        manufacturer_name: manufacturer,
        show_customer: false,
        other_id: getComponentActivationId(componentCode),
        authored_by: authoredBy,
        userData: {
          quantity: componentsWithQty[componentCode].qty,
        },
      })
      other.reloadSpecs()
      window.editor.addObject(other, system)
    })
  }

  if (otherComponentCodesToActivate.length) {
    window.AccountHelper.activateComponents(otherComponentCodesToActivate, addComponentsToProject)
  } else {
    addComponentsToProject()
  }
}

function showArchivedComponentsWarning(systemUuid: string, archivedCodes: string[]) {
  window.WorkspaceHelper.addProjectErrorToReduxStore({
    message: window.translate(
      'Some mounting components are archived and were not added to the system. Go to Control > Other Components to unarchive them. Archived: %{codes}',
      { codes: archivedCodes.join(', ') }
    ),
    key: 'MOUNTING_ARCHIVED_COMPONENTS',
    severity: 'warning',
    systemId: systemUuid,
    source: 'mounting-integration',
    category: 'mounting',
    options: {},
  })
}

export function saveRoofFacetData(system, result: MountingCalcResult, mcsData: any): void {
  const roofIndex = result.roofIndex
  const roofFacetData: any = {}

  // Handle fastener data
  Object.keys(result.fasteners).forEach((index) => {
    const fasteners: FastenerData[] = Object.values(result.fasteners[index])
    const avgFastenerData = fasteners.reduce(
      (accumulator: FastenerData, currentValue: FastenerData) => ({
        diameter:
          (accumulator.diameter * (accumulator.qty || 0) + currentValue.diameter * (currentValue.qty || 0)) /
          ((accumulator.qty || 0) + (currentValue.qty || 0)),
        length:
          (accumulator.length * (accumulator.qty || 0) + currentValue.length * (currentValue.qty || 0)) /
          ((accumulator.qty || 0) + (currentValue.qty || 0)),
        qty: (accumulator.qty || 0) + (currentValue.qty || 0),
      }),
      { length: 0, diameter: 0, qty: 0 } as FastenerData
    ) as FastenerData

    roofFacetData.fixing_length_screwed_through_timber = avgFastenerData.length - 25
    roofFacetData.fixing_diameter = avgFastenerData.diameter
    if (avgFastenerData.qty) roofFacetData.fixing_qty = avgFastenerData.qty
  })

  // Handle mounting weight data
  let mountingWeight = 0
  result.items.forEach((item) => {
    const matchedComponents = window.AccountHelper.getComponentOtherSpecsAvailable().filter(
      (component) => component.code === item.name
    )
    if (matchedComponents.length && matchedComponents[0].weight) mountingWeight += matchedComponents[0].weight
  })
  roofFacetData.mounting_weight = Math.round(mountingWeight)

  // Update the mcsData copy directly instead of the form
  if (!mcsData.roof_facets) mcsData.roof_facets = []
  if (!mcsData.roof_facets[roofIndex]) {
    mcsData.roof_facets[roofIndex] = {}
  }
  Object.assign(mcsData.roof_facets[roofIndex], roofFacetData)
}

export function setPanelSpacing(system, result, suppressObjectChanged = false) {
  const isSolarNestDualTiltRooftop =
    system.mounting === 'solarNestRooftop' && system.integration_json?.solarNestRooftop?.tiltOption === 'eastWestTilt'
  const isSolarNestDualTiltGroundMount =
    system.mounting === 'solarNestGroundMount' &&
    system.integration_json?.solarNestGroundMount?.tiltOption === 'eastWestTilt'
  const isSolarNestDualTilt = isSolarNestDualTiltRooftop || isSolarNestDualTiltGroundMount
  result.panelBlocks.forEach((panelBlock) => {
    const moduleGrid = window.editor.objectByUuid(panelBlock.uuid)
    if (JSON.stringify(moduleGrid.moduleSpacing) !== JSON.stringify(panelBlock.panelSpacing)) {
      let spacingToSet = panelBlock.panelSpacing
      if (isSolarNestDualTilt && moduleGrid.moduleSpacing && Array.isArray(moduleGrid.moduleSpacing)) {
        spacingToSet = [panelBlock.panelSpacing[0], moduleGrid.moduleSpacing[1]]
      }
      window.editor.execute(new window.SetPanelConfigurationCommand(moduleGrid, 'moduleSpacing', spacingToSet))
    }
  })
}

/** Set relative tilt to the given degrees for all module grids that use tilt racks. */
export function setRelativeTiltForModuleGrids(
  system: { moduleGrids(): { getSlope(): number; isUsingTiltRacks(): boolean; panelTiltOverride: number | null }[] },
  relativeTiltDegrees: number
) {
  system.moduleGrids().forEach((moduleGrid) => {
    if (!moduleGrid.isUsingTiltRacks()) return
    const slope = Math.round(moduleGrid.getSlope() * 10) / 10
    const panelTiltOverride = slope + relativeTiltDegrees
    const roundedTarget = Math.round(panelTiltOverride * 10) / 10
    const cur = moduleGrid.panelTiltOverride
    const roundedCur = cur != null && Number.isFinite(cur) ? Math.round(cur * 10) / 10 : null
    if (roundedCur === roundedTarget) {
      return
    }
    window.editor.execute(
      new window.SetPanelConfigurationCommand(
        moduleGrid,
        'panelTiltOverride',
        panelTiltOverride,
        window.Utils.generateCommandUUIDOrUseGlobal()
      )
    )
  })
}

/** Ground clearance in metres for every module grid on the system. */
export function setGroundClearanceForModuleGrids(
  system: { moduleGrids(): { getGroundClearance(): number }[] },
  groundClearanceMeters: number
) {
  system.moduleGrids().forEach((moduleGrid) => {
    if (moduleGrid.getGroundClearance() === groundClearanceMeters) {
      return
    }
    window.editor.execute(
      new window.SetPanelConfigurationCommand(
        moduleGrid,
        'groundClearance',
        groundClearanceMeters,
        window.Utils.generateCommandUUIDOrUseGlobal()
      )
    )
  })
}

/** Horiz. / vert. module spacing in metres (`moduleSpacing` is [x, y] in metres). */
export function setModuleSpacingForModuleGrids(
  system: { moduleGrids(): { moduleSpacing: number[] }[] },
  spacingMetres: [number, number]
) {
  system.moduleGrids().forEach((moduleGrid) => {
    const cur = moduleGrid.moduleSpacing
    if (
      Array.isArray(cur) &&
      cur.length >= 2 &&
      cur[0] === spacingMetres[0] &&
      cur[1] === spacingMetres[1]
    ) {
      return
    }
    window.editor.execute(
      new window.SetPanelConfigurationCommand(
        moduleGrid,
        'moduleSpacing',
        spacingMetres,
        window.Utils.generateCommandUUIDOrUseGlobal()
      )
    )
  })
}

/** Panel orientation (`moduleLayout`) for every module grid on the system. */
export function setModuleLayoutForModuleGrids(
  system: { moduleGrids(): { moduleLayout(): ModuleLayout }[] },
  moduleLayout: ModuleLayout
) {
  system.moduleGrids().forEach((moduleGrid) => {
    const prev = moduleGrid.moduleLayout()
    if (prev === moduleLayout) {
      return
    }
    window.editor.execute(new window.RedrawModulesCommand(moduleGrid, 'moduleLayout', moduleLayout, prev))
  })
}

export const getQuantityOfProducts = (qtyRule: QuantityPerProduct, prodCode: ProductCode): number | null =>
  parseInt(Object.keys(qtyRule).find((key) => qtyRule[key].includes(prodCode)) || '', 10)
